/**
 * House systems — the fab as one machine.
 *
 * Every module until now has been a closed world: a tool, its recipe, its own
 * hidden condition, its own faults. That is a fair model of a training bay and
 * a poor model of a fab. In a real building the tools hang off shared systems -
 * process vacuum, cooling water, the dry-gas header - and when one of those
 * goes, the symptom appears somewhere else entirely, on a tool that is
 * perfectly healthy, in front of a technician who has no reason to suspect the
 * sub-fab.
 *
 * THE SKILL THIS EXISTS TO TEACH
 *
 * "The tool in front of you is not the broken one" is the hardest diagnostic
 * call on a fab floor and the one with the worst failure mode: a technician who
 * cannot make it will keep repairing a healthy tool, correctly observing that
 * the symptom is still there, and escalating only after they have run out of
 * parts. Every local repair they try is real downtime spent on a tool that was
 * never at fault.
 *
 * Two fault descriptions in this codebase already promised this before any of
 * it existed - the etcher's vacuum leak and FAC-06's pinched door seal both
 * narrate a process technician upstairs chasing a mystery that a facilities
 * technician can see. This module is that narration made mechanical.
 *
 * HOW IT REACHES A TOOL
 *
 * Not by writing into the fields a local repair can restore. That distinction
 * is the entire design:
 *
 *   a tool's LOCAL condition fields are what its own aspects repair
 *   a tool's HOUSE-SUPPLIED fields are written from here and cannot be
 *     repaired at the tool at all
 *
 * Both reach the wafer through the same physics, so the symptom is genuinely
 * ambiguous. But the counterfactual is not: restoring the local aspect recovers
 * nothing, and the diagnosis machinery that already exists reports that without
 * needing to know anything about house systems. A student who calls "find the
 * leak on this chamber" spends eight hours and the tool comes back still
 * broken.
 *
 * WHAT IS DELIBERATELY NOT HERE
 *
 * The metrology tool has no house exposure - and no longer needs one for its
 * best cross-module story. "The reflectometer is innocent and the FILM
 * changed" used to be blocked on a composition model this codebase refused to
 * fake; that model now exists (metrology/product.ts: parasitic fraction ->
 * Bruggeman effective index, calibrated by sign rather than magnitude), rides
 * the wafer from DEP-02 via dep/handoff.ts, and is diagnosable at MET-05 as
 * the wrongIndex fault. The lesson travels on the LOT, not the plumbing,
 * which is why it lives there and not here.
 */

import { tOr } from '../../i18n/i18n'
import { DEFAULT_MAU, NOMINAL_MAU_CONDITION, mauHouse, type MauCondition } from '../facilities/MauSim'

/**
 * The shared systems, as the quantities a tool actually feels.
 *
 * Each field is expressed at the TOOL rather than at the plant, because that is
 * where it has to be added to a local condition. A leak in the header is not
 * described here in Torr-litres per second; it is described as the leak-up rate
 * it adds at every chamber hanging off it.
 */
export interface HouseState {
  /** Leak-up the shared process vacuum header adds at a tool [mTorr/min]. */
  vacuumHeaderLeak: number
  /** Background pressure the header adds at a tool [mTorr]. */
  vacuumHeaderMtorr: number
  /** Process cooling water supply temperature minus nominal [C]. */
  pcwOffsetC: number
  /** Water in the house dry-gas header [ppm]. */
  gasHeaderPpm: number
  /** Ultrapure water resistivity at the point of use, 25 °C-compensated [MΩ·cm]. */
  upwMohmCm: number
  /** Air the recirculation handlers deliver, as a fraction of design flow. */
  ahuDelivery: number
  /**
   * Outside air the make-up unit delivers, as a fraction of design flow. The
   * recirculation handlers turn the cleanroom's air over; this is the air
   * that REPLACES what the exhaust fans throw away, and the pressure cascade
   * is the balance between the two.
   */
  mauDelivery: number
  /** Cleanroom pressure against ambient [Pa], what that balance comes to. */
  cleanroomPa: number
  /** Cleanroom relative humidity [%], what the make-up unit's humidifier holds. */
  cleanroomRh: number
  /**
   * The tool whose dedicated dry pump has failed, by asset tag, or null when
   * every pump is pulling. Each tool upstairs has its own pump down here; a
   * failed one starves exactly one tool, which is what tells it apart from
   * the shared header leaking.
   */
  dryPumpFailedTool: string | null
  /** What that pump's foreline reads [Torr]: 50 mTorr pulling, atmosphere seized. */
  dryPumpForelineTorr: number
  /**
   * The tool whose chemistry delivery line has leaked into its containment,
   * by asset tag, or null when every tray is dry. A leak trips the tray
   * sensor and the interlock shuts that line's delivery, so the tool it
   * feeds stops being made up - the one tool, because each line is its own.
   */
  chemLeakTool: string | null
  /** What the leaking line's tray sensor has collected [L]. */
  chemLeakLitres: number
  /** That line's delivery, as a fraction of demand: 1 flowing, 0 interlocked shut. */
  chemDeliveryFraction: number
}

/** The tools whose dry pumps stand in the sub-fab; the layout harness holds it equal to the gear. */
export const DRY_PUMPED_TOOLS = ['MET-05', 'INSP-13', 'PRB-14'] as const
/** The tools fed chemistry from a cabinet in the sub-fab; likewise held equal to the gear. */
export const CHEM_DELIVERED_TOOLS = ['CMP-11', 'ECD-12'] as const

export const NOMINAL_HOUSE: HouseState = {
  vacuumHeaderLeak: 0,
  vacuumHeaderMtorr: 0,
  pcwOffsetC: 0,
  // A dry-gas header is never at zero. Two parts per million is a good house.
  gasHeaderPpm: 2,
  // A healthy polish loop delivers within a breath of the 25 °C theoretical max.
  upwMohmCm: 18.2,
  ahuDelivery: 1,
  // The make-up unit at its design point, clean: +15 Pa, 45 % RH. Derived
  // from the unit's own model rather than restated, so the two cannot drift.
  ...mauHouse(DEFAULT_MAU, NOMINAL_MAU_CONDITION),
  dryPumpFailedTool: null,
  dryPumpForelineTorr: 0.05,
  chemLeakTool: null,
  chemLeakLitres: 0,
  chemDeliveryFraction: 1,
}

export type HouseSystem = 'vacuum' | 'pcw' | 'gas' | 'upw' | 'hvac' | 'mau' | 'pumps' | 'chem'

export interface HouseSystemInfo {
  id: HouseSystem
  label: string
  /** What facilities actually does about it, and how long the tool waits. */
  action: string
  hours: number
}

export const HOUSE_SYSTEMS: Record<HouseSystem, HouseSystemInfo> = {
  vacuum: {
    id: 'vacuum',
    label: 'House process vacuum',
    action: 'Escalate to facilities: leak-check the shared vacuum header',
    hours: 8,
  },
  pcw: {
    id: 'pcw',
    label: 'Process cooling water',
    action: 'Escalate to facilities: restore chilled water supply temperature',
    hours: 6,
  },
  gas: {
    id: 'gas',
    label: 'House dry-gas header',
    action: 'Escalate to facilities: change the header dryer, purge and re-certify',
    hours: 5,
  },
  upw: {
    id: 'upw',
    label: 'Ultrapure water plant',
    action: 'Escalate to facilities: regenerate the polishing mixed bed and re-qualify the loop',
    hours: 8,
  },
  hvac: {
    id: 'hvac',
    label: 'Cleanroom air handling',
    action: 'Escalate to facilities: restore the recirculation handlers to design flow',
    hours: 6,
  },
  mau: {
    id: 'mau',
    label: 'Make-up air unit',
    action: 'Escalate to facilities: change the make-up air pre-filters and re-balance the cascade',
    hours: 4,
  },
  pumps: {
    id: 'pumps',
    label: 'Tool dry pumps',
    action: 'Escalate to facilities: swap the dry pump under the tool and re-qualify its foreline',
    hours: 3,
  },
  chem: {
    id: 'chem',
    label: 'Chemical delivery',
    action: 'Escalate to facilities: isolate the leaking line, replace the inner pipe and re-certify the containment',
    hours: 5,
  },
}

/**
 * The house faults, defined once.
 *
 * Defined once is the point. A wet header is one event in the building, and the
 * furnace and the ALD tool are two different consequences of that same event -
 * so they have to be derived from the same numbers, or the demonstration is a
 * coincidence rather than a mechanism.
 */
export interface HouseFault {
  id: string
  label: string
  system: HouseSystem
  /** What facilities finds when someone finally calls them. */
  truth: string
  state: HouseState
  /** For an event on the make-up air unit: the condition the bench shows while it is armed. */
  mau?: MauCondition
}

/** The loaded-filter event, as the unit's own condition - one place, two readers. */
const LOADED_PREFILTERS: MauCondition = { filterLoading: 0.45 }

export const HOUSE_FAULTS: HouseFault[] = [
  {
    id: 'headerLeak',
    label: 'Leak in the shared process vacuum header',
    system: 'vacuum',
    truth:
      'A flange on the shared process vacuum header was disturbed during the sub-fab work last ' +
      'weekend and is leaking. Every chamber on that header sees it as its own leak - because ' +
      'from inside the chamber, it is one.',
    state: { ...NOMINAL_HOUSE, vacuumHeaderLeak: 26, vacuumHeaderMtorr: 3.2 },
  },
  {
    id: 'warmPcw',
    label: 'Process cooling water running warm',
    system: 'pcw',
    truth:
      'The chilled water plant is delivering supply water about 22 C above nominal. Nothing on ' +
      'any tool is broken; every tool that rejects heat into that loop has simply stopped being ' +
      'able to hold its own setpoint.',
    state: { ...NOMINAL_HOUSE, pcwOffsetC: 22 },
  },
  {
    id: 'wetHeader',
    label: 'Moisture in the house dry-gas header',
    system: 'gas',
    truth:
      'The house dry-gas dryer failed wet over the weekend and the header is carrying about 2% ' +
      'water - carryover, not the ppm-level creep a saturated dryer gives. It reaches every tool ' +
      'that breathes house nitrogen or oxygen, and what it does when it gets there depends ' +
      'entirely on what that tool was using the gas for. Note how far apart the two thresholds ' +
      'are: ALD is ruined by single-figure ppm, because water is its co-reactant, while a furnace ' +
      'growing oxide in steam does not notice anything below about a percent. It takes a gross ' +
      'failure for both tools to complain at once, and that is exactly why the two groups rarely ' +
      'connect their problems.',
    state: { ...NOMINAL_HOUSE, gasHeaderPpm: 20000 },
  },
  {
    id: 'spentBed',
    label: 'Polishing mixed bed exhausted',
    system: 'upw',
    truth:
      'The polishing mixed bed on the ultrapure water loop is at 91% of its exchange capacity ' +
      'and sodium is breaking through: the loop that read 18.1 MΩ·cm last week reads 11 today. ' +
      'Nobody watched it coming because there was nothing to watch - an ion-exchange bed holds ' +
      'the meter at 18.2 for almost its whole life and then fails across the last few percent, ' +
      'which is why you regenerate on throughput, not on the reading. The rinse is the LAST ' +
      'thing a wafer sees at the wet bench, so the bench reads as having failed both of its ' +
      'chemistries at once - a signature no bath on that tool can produce.',
    state: { ...NOMINAL_HOUSE, upwMohmCm: 11 },
  },
  {
    id: 'ahuBeltSlip',
    label: 'Recirculation air handlers under-delivering',
    system: 'hvac',
    truth:
      'Two of the four recirculation air handlers have slipping belts and the bays are getting ' +
      'about 55% of design airflow. Every filter in the ceiling is fine, every fan up there is ' +
      'fine - the air simply is not arriving. The room reads dirtier at the same headcount, and ' +
      'the number that gives it away is RECOVERY: how long the bay takes to swallow an upset is ' +
      'set by air changes and nothing else, so a slow recovery with clean filters is the ' +
      'building, not the room. Changing the ceiling filters up here costs twelve hours and ' +
      'recovers nothing.',
    state: { ...NOMINAL_HOUSE, ahuDelivery: 0.55 },
  },
  {
    id: 'mauFiltersLoaded',
    label: 'Make-up air pre-filters loaded',
    system: 'mau',
    truth:
      'The make-up air unit’s pre-filters have loaded up over a season of pollen and ' +
      'construction dust and the unit is delivering about 55% of design outside air. The ' +
      'recirculation handlers are fine, so every bay still turns its air over at full rate - ' +
      'but make-up air is what the exhaust fans are balanced against, and with the balance gone ' +
      'the whole cleanroom has dropped below the corridor. Every bay reads negative at once and ' +
      'pulls corridor air in through every gap, and nothing any bay can do to its own exhaust ' +
      'restores a cascade the building has lost. Changing the pre-filters downstairs takes four ' +
      'hours.',
    state: { ...NOMINAL_HOUSE, ...mauHouse(DEFAULT_MAU, LOADED_PREFILTERS) },
    mau: LOADED_PREFILTERS,
  },
  {
    id: 'dryPumpFailed',
    label: 'Dry pump under PRB-14 failed',
    system: 'pumps',
    truth:
      'The dry pump under PRB-14 has run its bearings and seized: the foreline it used to hold ' +
      'at 50 mTorr is sitting at 600 Torr, a hair under atmosphere. The prober holds every wafer ' +
      'on its chuck by vacuum, and with a fifth of the hold it should have, the wafer sits loose ' +
      '- flat where the chuck’s centre seal still grips, lifting at the edge where it does not. ' +
      'The card lands light on the lifted die and they fail contact, which looks exactly like a ' +
      'card out of plane; but the card is fine, the tips are fine, and no other tool on the floor ' +
      'has moved, because no other tool shares this pump. Swapping the pump downstairs takes ' +
      'three hours.',
    state: { ...NOMINAL_HOUSE, dryPumpFailedTool: 'PRB-14', dryPumpForelineTorr: 600 },
  },
  {
    id: 'chemLineLeak',
    label: 'Plating chemistry line leaking under ECD-12',
    system: 'chem',
    truth:
      'The inner pipe of the plating chemistry line under ECD-12 has split inside its containment ' +
      'sleeve. The sleeve did its job: the chemistry ran down to the tray under the cabinet, the ' +
      'tray sensor tripped at four litres, and the leak interlock shut the delivery pump - which ' +
      'is exactly what it is for, because plating chemistry on a sub-fab floor is an acid spill. ' +
      'Upstairs the bath has not been made up all shift, and plating pulls copper out of it with ' +
      'every wafer, so the cell is running on a third of the copper its recipe assumes. No other ' +
      'tool has moved: each line is its own. Isolating the line, replacing the inner pipe and ' +
      're-certifying the containment takes five hours.',
    state: { ...NOMINAL_HOUSE, chemLeakTool: 'ECD-12', chemLeakLitres: 4.2, chemDeliveryFraction: 0 },
  },
]

export const houseFault = (id: string): HouseFault =>
  HOUSE_FAULTS.find((f) => f.id === id) ?? HOUSE_FAULTS[0]

/**
 * The reading a technician gets by picking up the phone.
 *
 * Deliberately one reading covering all three systems, and deliberately earned
 * rather than free. A tool that displayed its incoming utilities on the front
 * panel would make every scenario here trivial, and some tools really do. The
 * ones that do not are the ones this is about.
 */
export function houseUtilityValue(house: HouseState): string {
  return (
    `header ${house.vacuumHeaderLeak > 1 ? 'LEAKING' : 'tight'}, ` +
    `PCW ${house.pcwOffsetC >= 0 ? '+' : ''}${house.pcwOffsetC.toFixed(0)} C, ` +
    `dry gas ${house.gasHeaderPpm.toFixed(0)} ppm, ` +
    `UPW ${house.upwMohmCm.toFixed(1)} MΩ·cm, ` +
    `air ${(house.ahuDelivery * 100).toFixed(0)}% of design, ` +
    `make-up ${(house.mauDelivery * 100).toFixed(0)}% (room ${house.cleanroomPa >= 0 ? '+' : ''}${house.cleanroomPa.toFixed(0)} Pa), ` +
    `dry pumps ${house.dryPumpFailedTool ? `${house.dryPumpFailedTool} DOWN` : 'all pulling'}, ` +
    `chemistry ${house.chemLeakTool ? `${house.chemLeakTool} LINE LEAKING, delivery shut` : 'trays dry'}`
  )
}

/** One system's health, from the same thresholds the roll-up uses. */
export function houseSystemState(
  house: HouseState,
  system: HouseSystem,
): 'good' | 'warning' | 'critical' {
  const grade = (bad: boolean, marginal: boolean) =>
    bad ? 'critical' : marginal ? 'warning' : 'good'
  switch (system) {
    case 'vacuum':
      return grade(house.vacuumHeaderLeak > 5, house.vacuumHeaderLeak > 1)
    case 'pcw':
      return grade(Math.abs(house.pcwOffsetC) > 10, Math.abs(house.pcwOffsetC) > 3)
    case 'gas':
      return grade(house.gasHeaderPpm > 200, house.gasHeaderPpm > 50)
    case 'upw':
      return grade(house.upwMohmCm < 14, house.upwMohmCm < 17.5)
    case 'hvac':
      return grade(house.ahuDelivery < 0.7, house.ahuDelivery < 0.9)
    case 'mau':
      // The cascade is the number: +5 Pa is the floor a cleanroom must hold.
      return grade(house.cleanroomPa < 5, house.cleanroomPa < 10 || house.mauDelivery < 0.9)
    case 'pumps':
      // A dry pump either pulls or it does not; a foreline drifting up is the warning.
      return grade(house.dryPumpFailedTool !== null, house.dryPumpForelineTorr > 0.2)
    case 'chem':
      // A wet tray is the fault; anything in a tray at all is the warning.
      return grade(house.chemLeakTool !== null, house.chemLeakLitres > 0)
  }
}

export function houseUtilityState(house: HouseState): 'good' | 'warning' | 'critical' {
  const states = (Object.keys(HOUSE_SYSTEMS) as HouseSystem[]).map((s) =>
    houseSystemState(house, s),
  )
  return states.includes('critical') ? 'critical' : states.includes('warning') ? 'warning' : 'good'
}

/**
 * The one house, shared by every module for the length of a session.
 *
 * A house event is a fact about the BUILDING, so it cannot live inside a tool
 * panel. Arming one here and letting each tool subscribe is what turns the
 * cross-module lesson from a claim in a fault description into something a
 * student can walk to: arm a wet header at facilities, walk to the ALD tool and
 * find growth running high, walk to the furnace and find the oxide thick, and
 * discover on the way that those two symptoms have nothing in common except a
 * cause.
 *
 * Tools that do not feel a given event go back to a healthy shift when it is
 * armed, which is information too - most of the fab is fine, and knowing which
 * part is not is most of the diagnosis.
 */
export class HouseBus {
  private faultId: string | null = null
  private readonly listeners = new Set<(id: string | null) => void>()

  /** Id of the armed house event, or null when the house is healthy. */
  get armed(): string | null {
    return this.faultId
  }

  get state(): HouseState {
    return this.faultId ? houseFault(this.faultId).state : NOMINAL_HOUSE
  }

  arm(id: string | null): void {
    this.faultId = id
    for (const listener of [...this.listeners]) listener(id)
  }

  subscribe(listener: (id: string | null) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }
}

/** One facility-side gauge on a skid. */
export interface SkidReading {
  id: string
  label: string
  value: string
  state: 'good' | 'warning' | 'critical'
}

/**
 * What the gauges on one plant's skid actually show, from the live house.
 *
 * The facilities technician's half of the diagnosis: upstairs every reading
 * is a symptom, down here every reading is a cause. The innocent plants must
 * read healthy under any armed event - most of the sub-fab being fine, and
 * knowing which part is not, is most of the job - and the harness asserts
 * exactly that for every house fault.
 */
export function skidReadings(system: HouseSystem, house: HouseState): SkidReading[] {
  const state = houseSystemState(house, system)
  switch (system) {
    case 'vacuum':
      return [
        {
          id: 'headerLeakUp',
          label: 'Header leak-up rate',
          value: `${house.vacuumHeaderLeak.toFixed(1)} mTorr/min`,
          state,
        },
        {
          id: 'headerAdder',
          label: 'Pressure added at each chamber',
          value: `${house.vacuumHeaderMtorr.toFixed(1)} mTorr`,
          state,
        },
      ]
    case 'pcw':
      return [
        {
          id: 'pcwSupply',
          label: 'Supply temperature vs nominal',
          value: `${house.pcwOffsetC >= 0 ? '+' : ''}${house.pcwOffsetC.toFixed(0)} °C`,
          state,
        },
      ]
    case 'gas':
      return [
        {
          id: 'gasMoisture',
          label: 'Header moisture at the dryer outlet',
          value: `${house.gasHeaderPpm.toFixed(0)} ppm`,
          state,
        },
      ]
    case 'upw':
      return [
        {
          id: 'upwLoop',
          label: 'Loop resistivity, 25 °C compensated',
          value: `${house.upwMohmCm.toFixed(1)} MΩ·cm`,
          state,
        },
      ]
    case 'hvac':
      return [
        {
          id: 'ahuFlow',
          label: 'Delivered airflow vs design',
          value: `${(house.ahuDelivery * 100).toFixed(0)}%`,
          state,
        },
        {
          id: 'ahuBelts',
          label: 'Drive belt inspection',
          value:
            house.ahuDelivery < 0.9
              ? tOr('subfab.BeltsSlipping', 'slipping under load')
              : tOr('subfab.BeltsTight', 'tight'),
          state,
        },
      ]
    case 'pumps':
      // One foreline gauge per pump. The failed one reads near atmosphere;
      // the innocent ones keep pulling, which is the whole diagnosis.
      return DRY_PUMPED_TOOLS.map((tag) => {
        const failed = house.dryPumpFailedTool === tag
        const torr = failed ? house.dryPumpForelineTorr : NOMINAL_HOUSE.dryPumpForelineTorr
        return {
          id: `foreline${tag.replace('-', '')}`,
          label: `Foreline pressure, ${tag}`,
          value: torr >= 1 ? `${torr.toFixed(0)} Torr` : `${(torr * 1000).toFixed(0)} mTorr`,
          state: failed ? 'critical' : 'good',
        }
      })
    case 'chem':
      // One tray sensor per cabinet, and the interlock that answers it.
      return [
        ...CHEM_DELIVERED_TOOLS.map((tag) => {
          const leaking = house.chemLeakTool === tag
          return {
            id: `tray${tag.replace('-', '')}`,
            label: `Containment tray, ${tag} line`,
            value: leaking ? `${house.chemLeakLitres.toFixed(1)} L collected` : tOr('subfab.TrayDry', 'dry'),
            state: leaking ? ('critical' as const) : ('good' as const),
          }
        }),
        {
          id: 'chemInterlock',
          label: 'Delivery interlock',
          value: house.chemLeakTool
            ? tOr('subfab.InterlockShut', 'SHUT - {{tag}} delivery stopped').replace('{{tag}}', house.chemLeakTool)
            : tOr('subfab.InterlockOpen', 'open, all lines delivering'),
          state,
        },
      ]
    case 'mau': {
      // The pre-filter's pressure drop is what a loaded filter shows a
      // technician: 60 Pa clean, climbing past the 200 Pa change point as
      // it blinds. Read off the delivery shortfall the loading causes.
      const loading = 1 - house.mauDelivery
      return [
        {
          id: 'mauFlow',
          label: 'Make-up air delivered vs design',
          value: `${(house.mauDelivery * 100).toFixed(0)}%`,
          state,
        },
        {
          id: 'mauFilterDp',
          label: 'Pre-filter pressure drop',
          value: `${(60 + 380 * Math.max(0, loading)).toFixed(0)} Pa`,
          state,
        },
        {
          id: 'roomPressure',
          label: 'Cleanroom pressure vs ambient',
          value: `${house.cleanroomPa >= 0 ? '+' : ''}${house.cleanroomPa.toFixed(0)} Pa`,
          state,
        },
      ]
    }
  }
}

/**
 * What repairing one plant achieves, given what is actually armed.
 *
 * Pure on purpose: the decision is testable without a scene or a bus. The
 * asymmetry is the lesson - the repair always costs its hours, and only the
 * plant that is actually sick recovers anything. Repairing a healthy skid is
 * the facilities-side version of repairing a healthy tool, and it is charged
 * exactly the same way.
 */
export function repairOutcome(
  system: HouseSystem,
  armedId: string | null,
): { clears: boolean; hours: number; action: string } {
  const info = HOUSE_SYSTEMS[system]
  return {
    clears: armedId !== null && houseFault(armedId).system === system,
    hours: info.hours,
    action: info.action,
  }
}
